/**
 * US phone numbers as a person reads them, "(555) 555-0123", built up as
 * they type. The API takes this spelling everywhere: a mobile is normalised
 * to E.164 (`normalisePhone`), a facility's or administrator's phone is
 * kept as written. A number typed with a country code other than +1 is
 * left exactly as typed, so a foreign number is never mangled.
 */

export const US_PHONE_PLACEHOLDER = '(555) 555-0123'

const US_DIGITS = 10

/** True when the text is an international number that is not North American. */
export function isInternationalPhone(raw: string): boolean {
  const trimmed = raw.trimStart()
  return trimmed.startsWith('+') && !/^\+\s*1/.test(trimmed) && /\d/.test(trimmed)
}

/** True when the text's first digit is the country code 1: no US area code begins with 1, so a leading 1 never belongs to the number. */
export function hasUsCountryCode(raw: string): boolean {
  return raw.replace(/\D/g, '').startsWith('1')
}

/** The national digits of a US number, at most ten, without a leading country code 1. */
export function usPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  return (hasUsCountryCode(raw) ? digits.slice(1) : digits).slice(0, US_DIGITS)
}

/** "(555) 555-0123" from any spelling of the digits, partially while they are still being typed. */
export function formatUsPhone(raw: string): string {
  if (isInternationalPhone(raw)) {
    return raw
  }
  const digits = usPhoneDigits(raw)
  if (digits.length === 0) {
    // "+" or "+1" on its way to a number: kept, so the person sees what they typed.
    return raw.trimStart().startsWith('+') ? raw.trim() : ''
  }
  if (digits.length <= 3) {
    return `(${digits}`
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** Empty, international, or all ten digits: anything else is a number still being typed. */
export function isCompleteUsPhone(raw: string): boolean {
  return raw.trim() === '' || isInternationalPhone(raw) || usPhoneDigits(raw).length === US_DIGITS
}

/** Where the caret belongs in `formatted` to sit after the same number of digits it followed before. */
export function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) {
    return formatted.startsWith('(') ? 1 : 0
  }
  let seen = 0
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index] ?? '')) {
      seen += 1
      if (seen === digitsBefore) {
        return index + 1
      }
    }
  }
  return formatted.length
}
